import type { FunctionComponent, SyntheticEvent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { BadgeTheme } from '../../../Badge'
import type { ProductColour } from '../../../ColourSwatch/ColourSwatchGroup'
import { ColourSwatchGroup } from '../../../ColourSwatch/ColourSwatchGroup'
import type { ProductScoreGaugeProps } from '../../../ProductScoreGauge'
import { SaveButton } from '../../../SaveButton'
import { TypographyV2 } from '../../../Seatbelt2/Foundations/Typography'
import { ProductCardAdditionalInfo } from '../shared/ProductCardAdditionalInfo'
import { ProductCardBadges } from '../shared/ProductCardBadges'
import type { CCBProps } from '../shared/ProductCardCompareCheckbox/ProductCardCompareCheckbox'
import { ProductCardCompareCheckbox } from '../shared/ProductCardCompareCheckbox/ProductCardCompareCheckbox'
import { ProductCardPrice } from '../shared/ProductCardPrice/ProductCardPrice'
import { ProductCardTags } from '../shared/ProductCardTags/ProductCardTags'
import styles from './CompactProductCard.module.scss'

export const CompactProductCard: FunctionComponent<CompactProductCardProps> = ({
  manufacturer,
  model,
  badges,
  tags,
  price,
  retailers,
  handleAddCompare,
  handleRemoveCompare,
  productScoreGauge,
  isSelectedForComparison,
  comparisonLimitReached,
  cardCallback,
  slug,
  productUrl,
  viewRetailersCallback,
  businessKey,
  variants,
  selectedVariant,
  handleVariantChange,
  saved = false,
  saveOnClick,
  showCompare,
}) => (
  <article
    className={styles.card}
    {...(cardCallback && { onClick: cardCallback })}
  >
    <ProductCardBadges badges={badges} />
    <div className={styles.information}>
      <div className={styles.details} data-testid="product-details">
        <div className={styles.detailsContent}>
          <TypographyV2
            tag="h1"
            textStyle="sb-text-interface-heading-large"
            className={classnames(styles.manufacturer, styles.left)}
          >
            {manufacturer}
            <TypographyV2
              tag="span"
              textStyle="sb-text-body-default-regular"
              className={classnames(styles.model, styles.left)}
            >
              {model}
            </TypographyV2>
          </TypographyV2>
          {saveOnClick && (
            <div className={styles.saveButton}>
              <SaveButton
                selected={saved}
                onClick={saveOnClick}
                productName={`${manufacturer} ${model}`}
              />
            </div>
          )}
        </div>
        <ProductCardTags tags={tags} className={styles.compactTags} />
        {variants && handleVariantChange && (
          <div className={styles.colourSwatch}>
            <ColourSwatchGroup
              name={`colour-swatch-group-${businessKey}`}
              variants={variants}
              selectedVariant={selectedVariant as ProductColour[]}
              onChange={handleVariantChange}
              classNameRadioGroup={styles.radioGroup}
            />
          </div>
        )}
        <ProductCardPrice
          {...{
            price,
            productUrl,
            retailers,
            slug,
            viewRetailersCallback,
            manufacturer,
            model,
          }}
          className={styles.price}
        />
        <ProductCardCompareCheckbox
          className={classnames(styles.compare, styles.compactButton)}
          manufacturer={manufacturer}
          model={model}
          slug={slug}
          handleAddCompare={handleAddCompare}
          handleRemoveCompare={handleRemoveCompare}
          businessKey=""
          isSelectedForComparison={isSelectedForComparison}
          comparisonLimitReached={comparisonLimitReached}
          type="compact-product-card"
          showCompare={showCompare}
        />
      </div>
    </div>
    <ProductCardAdditionalInfo
      productScoreGauge={productScoreGauge}
      className={styles.additionalInformation}
    />
  </article>
)

///////// IMPLEMENTATION /////////

export type CompactProductCardProps = {
  /**
   *  Product manufacturer.
   */
  manufacturer: string
  /**
   *  Product model.
   */
  model: string
  /**
   *  Product price.
   */
  price: string
  /**
   *  Test score gauge.
   */
  productScoreGauge: ProductScoreGaugeProps
  /**
   *  Optional list of badges.
   */
  badges?: Array<BadgeTheme> | null
  /**
   *  Optional list of tags.
   */
  tags?: Array<string> | null
  /**
   *  Optional boolean to render internal retailers link.
   */
  retailers?: boolean
  /**
   *  Internal hyperlink used for retailers link.
   */
  slug: string
  /**
   * callback that will be fired when the Product Card is clicked on
   */
  cardCallback?: (
    e: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent
  ) => void
  /**
   * Full product url used for retailers link.
   */
  productUrl: string
  /**
   * Optional callback to be called when the View Retailers link is clicked
   */
  viewRetailersCallback?: (e: SyntheticEvent) => void
  /**
   * Product businessKey
   */
  businessKey?: string
  /**
   * Optional variants
   */
  variants?: ProductColour[][] | null
  /**
   * Optional selected variant
   */
  selectedVariant?: ProductColour[] | null
  /**
   * Optional callback to be called when colour variant is selected
   */
  handleVariantChange?: (selectedVariant: ProductColour[]) => void
  /**
   * Boolean to indicate if the user saved this product
   */
  saved: boolean
  /**
   * Callback function for when the heart icon is clicked
   */
  saveOnClick?: () => void
} & Pick<
  CCBProps,
  | 'comparisonLimitReached'
  | 'handleAddCompare'
  | 'handleRemoveCompare'
  | 'isSelectedForComparison'
  | 'showCompare'
>
